/**
 * AgendaAIPlanDialog — "Montar meu dia com IA" workflow.
 *
 * 1. On open: POST /agenda/plan-with-ai {date} → list of 1-6 suggestions
 *    with time, title, priority, reason, (optional) budget_number &
 *    school_name.
 * 2. Admin checks the ones to accept (default: all) and reviews briefly.
 * 3. Clicks "Adicionar ao cronograma" → POST /agenda/apply-ai-plan with
 *    the accepted items. Each one is persisted with ``ai_generated=true``
 *    (shows a sparkly chip in the list).
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Loader2, Check, Clock } from "lucide-react";

const TYPE_LABEL = {
  agendamento: "Agendamento",
  visita_orcamento: "Visita de orçamento",
  lembrete: "Lembrete",
};
const todayIso = () => new Date().toISOString().slice(0, 10);

export default function AgendaAIPlanDialog({ open, onOpenChange, onApplied }) {
  const [date, setDate] = useState(todayIso());
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [accepted, setAccepted] = useState(new Set());

  const generate = async (targetDate) => {
    setLoading(true);
    setSuggestions([]);
    setAccepted(new Set());
    try {
      const r = await api.post("/agenda/plan-with-ai", { date: targetDate });
      const list = r.data?.suggestions || [];
      setSuggestions(list);
      setAccepted(new Set(list.map((_, i) => i))); // default: all accepted
      if (list.length === 0) toast.message("A IA não encontrou prioridades claras — nada a sugerir");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "A IA não conseguiu montar o plano");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setDate(todayIso());
      generate(todayIso());
    }
  }, [open]);

  const toggle = (idx) =>
    setAccepted((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });

  const apply = async () => {
    const picks = suggestions.filter((_, i) => accepted.has(i));
    if (!picks.length) {
      toast.error("Selecione pelo menos uma sugestão");
      return;
    }
    setApplying(true);
    try {
      const r = await api.post("/agenda/apply-ai-plan", { date, items: picks });
      toast.success(`${(r.data?.created || []).length} itens adicionados ao cronograma`);
      onApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao aplicar o plano");
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] flex flex-col p-0 overflow-hidden" data-testid="agenda-ai-dialog">
        <DialogHeader className="p-4 pb-3 border-b border-slate-100">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-fuchsia-600" />
            Monta meu dia com IA
          </DialogTitle>
          <DialogDescription className="text-[11.5px]">
            Dê uma olhada nas sugestões. Desmarque o que não faz sentido e clique em
            <strong> Adicionar ao cronograma</strong>.
          </DialogDescription>
        </DialogHeader>

        {/* Date picker + regenerate */}
        <div className="px-4 pt-3 flex items-end gap-2">
          <div className="flex-1">
            <label className="block text-[11px] font-black uppercase text-slate-600 mb-1">Para o dia</label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-9 text-xs"
              data-testid="ai-plan-date"
            />
          </div>
          <Button
            onClick={() => generate(date)}
            disabled={loading}
            variant="outline"
            size="sm"
            className="h-9 border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50"
            data-testid="ai-plan-regenerate"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            <span className="ml-1 text-[11px] font-bold">Gerar</span>
          </Button>
        </div>

        {/* Suggestions */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {loading ? (
            <div className="py-10 text-center text-sm text-slate-500 flex flex-col items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> A IA está pensando no seu dia…
            </div>
          ) : suggestions.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500">
              <Sparkles className="w-8 h-8 mx-auto text-slate-300 mb-2" />
              Nenhuma sugestão. Tente outro dia ou crie manualmente.
            </div>
          ) : (
            suggestions.map((s, i) => {
              const checked = accepted.has(i);
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => toggle(i)}
                  data-testid={`ai-suggestion-${i}`}
                  className={`w-full text-left rounded-xl border p-3 flex items-start gap-3 transition-colors ${
                    checked
                      ? "bg-fuchsia-50 border-fuchsia-300"
                      : "bg-white border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <span
                    className={`w-5 h-5 mt-0.5 rounded border-2 shrink-0 flex items-center justify-center ${
                      checked
                        ? "bg-fuchsia-600 border-fuchsia-600 text-white"
                        : "border-slate-300"
                    }`}
                  >
                    {checked && <Check className="w-3 h-3" strokeWidth={3} />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {s.time && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10.5px] font-black">
                          <Clock className="w-2.5 h-2.5" /> {s.time}
                        </span>
                      )}
                      <span className="text-[10px] font-black text-fuchsia-700 uppercase tracking-wide">
                        {TYPE_LABEL[s.type] || s.type}
                      </span>
                      {s.priority === "alta" && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 text-[10.5px] font-bold">
                          Alta
                        </span>
                      )}
                    </div>
                    <div className="font-heading font-black text-[13.5px] text-slate-900 leading-tight mt-0.5">
                      {s.title || "(sem título)"}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap gap-x-2">
                      {s.school_name && <span>{s.school_name}</span>}
                      {s.budget_number && <span>· Nº {s.budget_number}</span>}
                    </div>
                    {s.observation && (
                      <p className="mt-1 text-[11px] text-slate-600 italic">💡 {s.observation}</p>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-100 p-3 flex gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={applying}
            className="flex-1"
          >
            Cancelar
          </Button>
          <Button
            onClick={apply}
            disabled={applying || suggestions.length === 0 || accepted.size === 0}
            data-testid="ai-plan-apply"
            className="flex-1 bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
          >
            {applying ? "Aplicando..." : `Adicionar ${accepted.size || ""} ao cronograma`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
